import type { FC } from 'react'
import React from 'react'
import type { MediaItem } from '@homeviews/models'
import { maybePlaceholderImage } from '@homeviews/utils'
import { mixpanelTrack } from '@homeviews/seo'
import { assetIcon } from './settings'

interface HeadCell {
  id: string
  label: string
  sortable?: boolean
  width?: number
}

export const headCells = (libraryType = ''): HeadCell[] => [
  {
    id: 'image',
    label:
      libraryType === 'resources' || libraryType === 'guides' ? '' : 'File',
    width:
      libraryType === 'resources' || libraryType === 'guides' ? 80 : undefined,
  },
  {
    id: 'filename',
    label: 'Filename',
  },
  {
    id: 'type',
    label: 'Type',
  },
  {
    id: 'file_size',
    label: 'File size',
  },
  {
    id: 'format',
    label: 'Format',
  },
  ...(libraryType !== 'images'
    ? [
        {
          id: 'uploaded',
          label: 'Uploaded',
        },
      ]
    : []),
  {
    id: 'actions',
    label: 'Actions',
  },
]

interface IActionsCell {
  id: number
  handleTableEditClick: (id: number) => void
  handleTableDeleteClick: (id: number) => void
  downloadBtn: boolean
  onDownloadBtnClick: () => void
}
const ActionsCell: FC<IActionsCell> = ({
  id,
  handleTableEditClick,
  handleTableDeleteClick,
  downloadBtn = false,
  onDownloadBtnClick,
}) => {
  return (
    <div className="l-table__actionsCell">
      <span
        className="l-table__link l-table__link--primary"
        onClick={() => {
          handleTableEditClick(id)
          if (downloadBtn) {
            onDownloadBtnClick()
          }
        }}
      >
        {downloadBtn ? 'Download' : 'Edit'}
      </span>
      {!downloadBtn ? (
        <span
          className="l-table__link l-table__link--delete"
          onClick={() => handleTableDeleteClick(id)}
        >
          Delete
        </span>
      ) : null}
    </div>
  )
}

interface IImageCell {
  format: string
  url?: string
  libraryType: string
}
const ImageCell: FC<IImageCell> = ({ format, libraryType, url }) => {
  if (libraryType === 'resources' || libraryType === 'guides') {
    return (
      <div className="l-table__imageWrapper l-table__iconWrapper">
        <img src={`assets/icons/files/${assetIcon(format)}.svg`} />
      </div>
    )
  }
  return (
    <div className="l-table__imageWrapper">
      <img src={maybePlaceholderImage(url)} alt="" />
    </div>
  )
}

export const createRows = (
  mediaItems: MediaItem[],
  handleTableEditClick,
  handleTableDeleteClick,
  libraryType,
) => {
  const handleDownloadBtnClick = (
    name: string,
    fileName: string,
    type: string,
    size: string,
    format: string,
    date: string,
  ) => {
    let eventKey = ''
    if (libraryType === 'guides') {
      eventKey = 'GUIDES_DOWNLOAD'
    } else if (libraryType === 'resources') {
      eventKey = 'RESOURCES_DOWNLOAD'
    }

    if (eventKey) {
      mixpanelTrack('GUIDES_DOWNLOAD', {
        Name: name,
        Filename: fileName,
        Type: type,
        Size: size,
        Format: format,
        'Uploaded date': date,
      })
    }
  }

  if (!mediaItems?.length) {
    return []
  }
  return mediaItems.map((mediaItem: MediaItem) =>
    createData(
      mediaItem,
      handleTableEditClick,
      handleTableDeleteClick,
      libraryType,
      handleDownloadBtnClick,
    ),
  )
}

function createData(
  media: any,
  handleTableEditClick,
  handleTableDeleteClick,
  libraryType = '',
  handleDownloadBtnClick,
) {
  const {
    id,
    url,
    file_name,
    type,
    title,
    name,
    file_size,
    format,
    created_at,
    date_created,
  } = media
  const uploadDate =
    date_created || created_at
      ? new Date(date_created || created_at).toLocaleDateString('en-GB')
      : null

  return [
    <React.Fragment key="id">{id}</React.Fragment>,
    <ImageCell
      key="image"
      format={format || media.ext}
      libraryType={libraryType}
    />,
    <React.Fragment key="name">{name || title || file_name}</React.Fragment>,
    <React.Fragment key="type">{type}</React.Fragment>,
    <React.Fragment key="size">{file_size || media.size}</React.Fragment>,
    <React.Fragment key="format">{format || media.ext}</React.Fragment>,
    ...(uploadDate
      ? [<React.Fragment key="date">{uploadDate}</React.Fragment>]
      : []),
    <ActionsCell
      key="actions"
      id={id}
      downloadBtn={libraryType === 'images' ? false : true}
      handleTableEditClick={handleTableEditClick}
      handleTableDeleteClick={handleTableDeleteClick}
      onDownloadBtnClick={() =>
        handleDownloadBtnClick(
          name || title,
          file_name,
          type,
          file_size || media.size,
          format || media.ext,
          uploadDate,
        )
      }
    />,
  ]
}
